import { functionUrl } from "@/lib/functions";
import { supabase } from "@/lib/supabase";
import type { MessagePayload } from "@shared/types/messages";

export async function triggerSummarization(
  roomId: string,
  messages: MessagePayload[],
  existingSummary: string | null,
  previousMessageCount: number,
): Promise<void> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    // Fire-and-forget — intentionally not awaited by callers
    // @wc-ignore
    void fetch(functionUrl("summarize-chat-memory"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        roomId,
        messages,
        existingSummary,
        previousMessageCount,
      }),
    });
  } catch {
    // Non-fatal — stale or missing summary is benign
    console.warn("Failed to trigger summarization. Will retry on next message.");
  }
}

export type StreamingStep = {
  tool: string;
  label: string;
  status: "running" | "done";
};

export type InvalidatePayload = { resource: string; containerId: string };

export type AiStreamMetaEvent = CreditWarningMetaEvent;

/**
 * CR-158: the orchestrator refused the turn with a 402 because the workspace
 * has no AI credits left (hard-stop threshold or a manual block). requestAiTurns
 * REJECTS with this so callers can show a dedicated "no credits left" state
 * instead of a generic failure with a Retry that can only fail again.
 *
 * `reason` is the server's `hard_stop_threshold` | `manual_block` (undefined
 * for a pre-CR-158 body). The message is never shown: callers render their own
 * copy from `code`.
 *
 * The @wc-ignore below is load-bearing: wuchale would otherwise rewrite the
 * literals into a catalog lookup that calls a React hook — inside a class
 * constructor that runs in plain async code, which throws (`useState` of
 * null) and turns this rejection into a generic fallback string.
 */
// @wc-ignore
export class AiCreditsExhaustedError extends Error {
  readonly code = "credits_exhausted";
  readonly reason: string | undefined;

  constructor(reason?: string) {
    super("This workspace has no AI credits left.");
    this.name = "AiCreditsExhaustedError";
    this.reason = reason;
  }
}

/**
 * CR-160: the orchestrator's stream failed because of its AI provider (empty
 * balance, invalid key, rate limit, outage) and it said so in the SSE `error`
 * frame: `code` is `provider_quota` | `provider_auth` | `provider_rate_limit`
 * | `provider_unavailable` (or a future `provider_*`), `retryable` tells
 * whether a manual retry of the same turn can succeed. requestAiTurns reports
 * it (`providerError`) when the stream ends on such a frame without a `final`.
 *
 * The server usually persisted a reply explaining the failure before the
 * frame, so callers look for that row first and only fall back to their own
 * failed state when it is missing. The message is never shown. Same
 * load-bearing @wc-ignore as AiCreditsExhaustedError.
 */
// @wc-ignore
export class AiProviderError extends Error {
  readonly code: string;
  readonly retryable: boolean;

  constructor(code: string, retryable: boolean) {
    super("The AI provider could not answer.");
    this.name = "AiProviderError";
    this.code = code;
    this.retryable = retryable;
  }
}

export interface CreditWarningMetaEvent {
  type: "credit_warning";
  status: "warning" | "sync" | "blocked";
  balanceRemaining: number;
  thresholdValue: number;
  unit: string;
  lastReconciledAt: string | null;
  /**
   * CR-158: set only on the replay of a 402 body — `manual_block` means the
   * balance may be healthy, so the consumer must not downgrade it to "sync".
   */
  reason?: string;
}

/** A queued turn's lifecycle — mirrors `AiChatTurnStatus` (ai_chat_turns.status). */
export type AcceptedTurnStatus = "queued" | "running" | "completed" | "failed" | "cancelled";

/**
 * CR-141: one durable AI turn the server queued for a human message — the
 * api-server's `AcceptedTurn` (routes/ai-chat-requests.ts). Copied, not
 * imported: runtime code sharing between the two sides is not wired.
 */
export interface AcceptedTurn {
  turnId: string;
  aiPersonaId: string;
  aiMessageId: string;
  turnIndex: number;
  status: AcceptedTurnStatus;
}

/**
 * How an observed stream ended. The stream is only an OBSERVER of a durable
 * turn (CR-141): none of these means the turn is lost.
 * - `final` — the run finished (`finalNotOk` says whether it had output);
 * - `provider_error` — the AI provider failed (CR-160, see `providerError`);
 * - `turn_requeued` — the run stopped before its model ran and the server
 *   queued it again (the cron worker answers shortly);
 * - `turn_not_run` — the server gave up on the turn before running it;
 * - `cancelled` — the user stopped the turn (CR-172): the server saved a
 *   "Response stopped" placeholder under the turn's aiMessageId;
 * - `dropped` — the connection ended early (navigation, reload, network, or
 *   the caller aborted the observer after a Stop): the turn keeps running
 *   server-side.
 */
export type AiStreamEnd =
  | "final"
  | "provider_error"
  | "turn_requeued"
  | "turn_not_run"
  | "cancelled"
  | "dropped";

export type AiTurnRequestResult =
  /** 202: the turns exist and run when the room frees up (nobody streams them). */
  | { kind: "queued"; turns: AcceptedTurn[] }
  /**
   * CR-220: also a 202 — the message addressed a human teammate, so no persona
   * answers it and nothing was queued. The pending bubble just goes away.
   * `turns` is always empty, so callers read `result.turns` whatever the kind.
   * Mirrors `AcceptSuppressedResponse` in backend/api-server/src/routes/ai-chat-requests.ts.
   */
  | { kind: "suppressed"; reason: "human_addressed"; turns: [] }
  /** The first turn ran in this request and was streamed to the handlers. */
  | {
      kind: "streamed";
      turns: AcceptedTurn[];
      /** The streamed turn (from the `accepted` frame); null if it never came. */
      turnId: string | null;
      aiMessageId: string | null;
      end: AiStreamEnd;
      finalNotOk: boolean;
      providerError: AiProviderError | null;
    };

export type AiTurnRequest = {
  /** The room the human message lives in (a chat room or a comment thread). */
  roomId: string;
  /** The project the turns are scoped and billed to. */
  containerId: string;
  /** The caller's actor — must be the author of `messageId`. */
  actorId: string;
  /** The human message the personas answer (already inserted by the client). */
  messageId: string;
  aiPersonaIds: string[];
  /** History followed by the user's message, as the model should see it. */
  messages: MessagePayload[];
  webSearchEnabled?: boolean;
  docsBaseUrl?: string;
  // CR-83: the text under the comment mark when the request comes from a
  // document comment thread.
  commentedPassage?: string;
};

export type AiStreamHandlers = {
  /** The `accepted` frame: which queued turn this stream is running. */
  onAccepted?: (accepted: { turnId: string; aiMessageId: string; turns: AcceptedTurn[] }) => void;
  onChunk?: (partialText: string) => void;
  onMeta?: (event: AiStreamMetaEvent) => void;
  onStep?: (step: { tool: string; label?: string; status: "running" | "done" }) => void;
  onInvalidate?: (payload: InvalidatePayload) => void;
  onThinking?: (reasoning: string, active: boolean) => void;
};

export type AiTurnRequestOptions = {
  /**
   * CR-172: aborts the OBSERVER fetch only (after a Stop, once the cancel is
   * posted). The server is the source of truth: aborting never stops a turn.
   */
  signal?: AbortSignal;
};

/**
 * Map a failed durable turn's `last_error` code (stable codes written by the
 * api-server runner) onto the failure kind the chat and docs cards render.
 */
export function classifyTurnFailure(
  lastError: string | null | undefined,
): "credits_exhausted" | "provider_error" | undefined {
  if (lastError === "credits_exhausted") return "credits_exhausted";
  if (lastError?.startsWith("provider_")) return "provider_error";
  return undefined;
}
/**
 * CR-141: the accept call failed before the server confirmed the turns (a
 * 4xx/5xx other than 402, or no response at all). Nothing may have been
 * queued, so the caller can retry: accept is idempotent on (messageId,
 * persona). The message is never shown. Same load-bearing @wc-ignore as
 * AiCreditsExhaustedError.
 */
// @wc-ignore
export class AiTurnRequestError extends Error {
  readonly code = "ai_turn_request_failed";
  readonly status: number | null;

  constructor(status: number | null) {
    super("The AI request could not be sent.");
    this.name = "AiTurnRequestError";
    this.status = status;
  }
}

/**
 * CR-141: ask the server to answer `messageId` with each persona in
 * `aiPersonaIds` (one durable turn each, answered in order). When the room is
 * idle the first turn runs in this request and its frames reach `handlers`;
 * otherwise the server answers 202 and the turns run in the background.
 *
 * Resolves on every accepted outcome — a dropped stream included, since the
 * turn keeps running server-side. Rejects with AiCreditsExhaustedError on a
 * 402 (CR-158) and AiTurnRequestError when the request was not accepted.
 */
export async function requestAiTurns(
  request: AiTurnRequest,
  handlers: AiStreamHandlers = {},
  options: AiTurnRequestOptions = {},
): Promise<AiTurnRequestResult> {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();
  if (sessionError || !session?.access_token) {
    throw new AiTurnRequestError(null);
  }

  let response: Response;
  try {
    // @wc-ignore
    response = await fetch(functionUrl("ai-chat-requests"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        roomId: request.roomId,
        containerId: request.containerId,
        actorId: request.actorId,
        messageId: request.messageId,
        aiPersonaIds: request.aiPersonaIds,
        messages: request.messages,
        webSearchEnabled: request.webSearchEnabled ?? false,
        docsBaseUrl: request.docsBaseUrl,
        commentedPassage: request.commentedPassage,
      }),
      signal: options.signal,
    });
  } catch {
    throw new AiTurnRequestError(null);
  }

  if (response.status === 402) {
    throw await readCreditsExhausted(response, handlers.onMeta);
  }
  if (response.status === 202) {
    return readAcceptedBody(response);
  }
  if (!response.ok || !response.body) {
    console.error("AI request failed with status", response.status);
    throw new AiTurnRequestError(response.status);
  }
  return readTurnStream(response.body, handlers);
}

/** The 202 body: queued turns, or (CR-220) a message no persona answers. */
async function readAcceptedBody(
  response: Response,
): Promise<Extract<AiTurnRequestResult, { kind: "queued" | "suppressed" }>> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // The turns exist server-side whatever the body says.
    return { kind: "queued", turns: [] };
  }
  if (!body || typeof body !== "object") return { kind: "queued", turns: [] };
  const { status, turns } = body as { status?: unknown; turns?: unknown };
  if (status === "suppressed") {
    // The only reason the server sends today; nothing was queued either way.
    return { kind: "suppressed", reason: "human_addressed", turns: [] };
  }
  return { kind: "queued", turns: Array.isArray(turns) ? turns.filter(isAcceptedTurn) : [] };
}

function isAcceptedTurn(value: unknown): value is AcceptedTurn {
  if (!value || typeof value !== "object") return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.turnId === "string" &&
    typeof t.aiPersonaId === "string" &&
    typeof t.aiMessageId === "string" &&
    typeof t.turnIndex === "number" &&
    typeof t.status === "string"
  );
}

type StreamEvent = {
  type: string;
  ok?: boolean;
  token?: string;
  text?: string;
  tool?: string;
  label?: string;
  output?: string;
  summary?: string;
  message?: string;
  resource?: string;
  containerId?: string;
  code?: unknown;
  retryable?: unknown;
  turnId?: unknown;
  aiMessageId?: unknown;
  turns?: unknown;
};

/** Reads the SSE frames of an accepted turn; never rejects. */
async function readTurnStream(
  body: ReadableStream<Uint8Array>,
  handlers: AiStreamHandlers,
): Promise<AiTurnRequestResult> {
  const { onAccepted, onChunk, onMeta, onStep, onInvalidate, onThinking } = handlers;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let chunkText = "";
  let turns: AcceptedTurn[] = [];
  let turnId: string | null = null;
  let aiMessageId: string | null = null;
  let receivedFinal = false;
  let finalNotOk = false;
  let providerError: AiProviderError | null = null;
  let turnEnd: "turn_requeued" | "turn_not_run" | "cancelled" | null = null;

  for (;;) {
    let readResult: ReadableStreamReadResult<Uint8Array>;
    try {
      readResult = await reader.read();
    } catch {
      // Connection dropped mid-stream: the turn keeps running server-side.
      break;
    }
    const { done, value } = readResult;
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6);
      if (payload === "[DONE]") continue;

      let event: StreamEvent;
      try {
        event = JSON.parse(payload) as StreamEvent;
      } catch {
        continue;
      }

      if (event.type === "accepted") {
        if (typeof event.turnId === "string" && typeof event.aiMessageId === "string") {
          turnId = event.turnId;
          aiMessageId = event.aiMessageId;
          turns = Array.isArray(event.turns) ? event.turns.filter(isAcceptedTurn) : [];
          onAccepted?.({ turnId, aiMessageId, turns });
        }
      } else if (event.type === "chunk" && event.token) {
        chunkText += event.token;
        onChunk?.(chunkText);
        // The final answer is now streaming into the bubble — clear any
        // transient reasoning line.
        onThinking?.("", false);
      } else if (event.type === "answer_reset") {
        // A tentatively-streamed turn turned out to be a tool call — pull back
        // what was shown in the bubble; it will reappear as transient reasoning.
        chunkText = "";
        onChunk?.(chunkText);
      } else if (event.type === "llm_start") {
        onThinking?.("", true);
      } else if (event.type === "llm_end") {
        onThinking?.("", false);
      } else if (event.type === "thinking") {
        // Live intermediate reasoning (replace semantics — full text each tick).
        onThinking?.(event.text ?? "", true);
      } else if (event.type === "tool_start" && event.tool) {
        onStep?.({ tool: event.tool, label: event.label, status: "running" });
      } else if (event.type === "tool_end" && event.tool) {
        onStep?.({ tool: event.tool, status: "done" });
      } else if (event.type === "progress" && event.summary) {
        onChunk?.(event.summary);
      } else if (event.type === "final") {
        receivedFinal = true;
        // CR-94: `ok` is authoritative. ok:false means nothing was saved.
        finalNotOk = event.ok === false;
        // Partial output of a failed run never stays in the bubble.
        onChunk?.(finalNotOk ? "" : (event.output ?? ""));
        onThinking?.("", false);
      } else if (event.type === "invalidate" && event.resource && event.containerId) {
        onInvalidate?.({ resource: event.resource, containerId: event.containerId });
      } else if (event.type === "credit_warning") {
        onMeta?.(toCreditWarning(event as Record<string, unknown>));
      } else if (event.type === "error") {
        // Log the structured code only — the message is server text the
        // caller never needs.
        const code = typeof event.code === "string" ? event.code : undefined;
        if (code?.startsWith("provider_")) {
          // CR-160: expected (the server usually saved a reply explaining it).
          providerError = new AiProviderError(code, event.retryable === true);
          console.warn("AI stream error:", code);
        } else if (code === "turn_requeued" || code === "turn_not_run") {
          turnEnd = code;
        } else if (code === "turn_cancelled") {
          // CR-172: the user stopped this turn — expected, not an error.
          turnEnd = "cancelled";
        } else {
          console.error("AI stream error:", code ?? "unknown");
        }
      }
    }
  }

  const end: AiStreamEnd = receivedFinal
    ? "final"
    : providerError
      ? "provider_error"
      : (turnEnd ?? "dropped");
  return {
    kind: "streamed",
    turns,
    turnId,
    aiMessageId,
    end,
    finalNotOk,
    providerError: receivedFinal ? null : providerError,
  };
}

/**
 * CR-172: what the cancel route did with each requested turn — the api-server's
 * `TurnCancelResult` (routes/ai-chat-turn-cancel.ts). Copied, not imported:
 * runtime code sharing between the two sides is not wired. Keep in sync.
 * - `cancelled` — it was still queued: it will never run, the placeholder is saved;
 * - `requested` — it is running: the runner stops within about a second;
 * - `finished` — it had already ended (a reply that is already saved stays);
 * - `not_found` — no such turn of this user in this room;
 * - `error` — that turn's cancel failed (retry is safe).
 */
export type TurnCancelResult = "cancelled" | "requested" | "finished" | "not_found" | "error";

/** CR-172: the cancel route's 200 body (copied from the api-server, keep in sync). */
export interface TurnCancelResponse {
  results: { turnId: string; result: TurnCancelResult }[];
}

export type AiTurnCancelRequest = {
  roomId: string;
  /** The project the turns are scoped to (the chat's containerId). */
  containerId: string;
  /** The caller's actor — only the requester can stop a turn (Q3). */
  actorId: string;
  /** The turns to stop (batched by cancelAiTurns). */
  turnIds: string[];
};

const TURN_CANCEL_RESULTS: ReadonlySet<string> = new Set<TurnCancelResult>([
  "cancelled",
  "requested",
  "finished",
  "not_found",
  "error",
]);

/**
 * CR-172: the cancel route accepts at most this many turn ids per call
 * (`MAX_TURNS_PER_CANCEL` in the api-server's routes/ai-chat-turn-cancel.ts —
 * copied, keep in sync).
 */
export const MAX_TURNS_PER_CANCEL = 5;

/** One call to the cancel route; the answered results keyed by lower-cased id. */
async function postTurnCancel(
  request: AiTurnCancelRequest,
  answered: Map<string, TurnCancelResult>,
): Promise<void> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    // @wc-ignore
    const response = await fetch(functionUrl("ai-chat-turn-cancel"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      console.warn("AI turn cancel failed with status", response.status);
      return;
    }
    const body: unknown = await response.json();
    const results = (body as Partial<TurnCancelResponse> | null)?.results;
    for (const entry of Array.isArray(results) ? results : []) {
      if (typeof entry?.turnId !== "string") continue;
      const result = TURN_CANCEL_RESULTS.has(entry.result) ? entry.result : "error";
      answered.set(entry.turnId.toLowerCase(), result);
    }
  } catch {
    console.warn("AI turn cancel could not be sent");
  }
}

/**
 * CR-172: ask the server to stop the given turns (any number: they are sent
 * in batches of MAX_TURNS_PER_CANCEL). Never rejects: the answer maps each
 * REQUESTED id to its result — keyed by turn id, never by position (the
 * server lower-cases and dedupes ids) — and a failed call reports its turns
 * as `error`.
 */
export async function cancelAiTurns(
  request: AiTurnCancelRequest,
): Promise<Map<string, TurnCancelResult>> {
  const answered = new Map<string, TurnCancelResult>();
  for (let start = 0; start < request.turnIds.length; start += MAX_TURNS_PER_CANCEL) {
    await postTurnCancel(
      { ...request, turnIds: request.turnIds.slice(start, start + MAX_TURNS_PER_CANCEL) },
      answered,
    );
  }
  return new Map(
    request.turnIds.map((turnId) => [turnId, answered.get(turnId.toLowerCase()) ?? "error"]),
  );
}

function toCreditWarning(warning: Record<string, unknown>): CreditWarningMetaEvent {
  const rawStatus = typeof warning.status === "string" ? warning.status : undefined;
  const status: CreditWarningMetaEvent["status"] =
    rawStatus === "warning" || rawStatus === "sync" || rawStatus === "blocked"
      ? rawStatus
      : "warning";
  return {
    type: "credit_warning",
    status,
    balanceRemaining: Number(warning.balanceRemaining ?? 0),
    thresholdValue: Number(warning.thresholdValue ?? 0),
    unit: typeof warning.unit === "string" ? warning.unit : "units",
    lastReconciledAt:
      typeof warning.lastReconciledAt === "string" ? warning.lastReconciledAt : null,
  };
}

/**
 * CR-158: read the orchestrator's 402 body. A body carrying the balance
 * numbers is replayed through onMeta as a `blocked` credit_warning — the same
 * event the SSE stream emits — so the composer lock and warning banner react
 * exactly as they would mid-stream. A legacy or unreadable body still yields
 * the typed error, just without numbers.
 */
async function readCreditsExhausted(
  response: Response,
  onMeta?: (event: AiStreamMetaEvent) => void,
): Promise<AiCreditsExhaustedError> {
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = await response.json();
    if (parsed && typeof parsed === "object") body = parsed as Record<string, unknown>;
  } catch {
    // Not JSON — keep the empty body.
  }

  const reason = typeof body.reason === "string" ? body.reason : undefined;

  if (typeof body.balanceRemaining === "number" && typeof body.thresholdValue === "number") {
    onMeta?.({
      type: "credit_warning",
      status: "blocked",
      balanceRemaining: body.balanceRemaining,
      thresholdValue: body.thresholdValue,
      unit: typeof body.unit === "string" ? body.unit : "units",
      lastReconciledAt: typeof body.lastReconciledAt === "string" ? body.lastReconciledAt : null,
      ...(reason ? { reason } : {}),
    });
  }

  return new AiCreditsExhaustedError(reason);
}
